import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RiAddLine } from "react-icons/ri";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { SegmentedControl } from "src/components/SegmentedControl";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { createOfferingCategories } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { ExampleOfferingCategoryCard } from "./ExampleOfferingCategoryCard";
import {
  EXAMPLE_OFFERING_CATEGORIES,
  getExampleOfferingCategory,
  optionsForCategoryKey,
} from "./offeringCategoryTemplates";

type Props = {
  enrollmentPeriodId: string;
  existingKeys: readonly string[];
};

export const QuickCreateOfferingCategoryButton: React.FC<Props> = ({
  enrollmentPeriodId,
  existingKeys,
}) => {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [creatingKey, setCreatingKey] = React.useState<string | null>(null);
  const [selectedKey, setSelectedKey] = React.useState(
    () => getExampleOfferingCategory("grade").key
  );
  const [keyDrafts, setKeyDrafts] = React.useState<Record<string, string>>({});

  const takenKeys = new Set(existingKeys.map((key) => key.trim()));
  const selected = getExampleOfferingCategory(selectedKey);
  const draftKey = keyDrafts[selected.key] ?? selected.key;
  const trimmedKey = draftKey.trim();
  const mustRename = takenKeys.has(selected.key);
  const categoryKey = mustRename ? trimmedKey : selected.key;
  const previewCategory = {
    ...selected,
    options: optionsForCategoryKey(selected, categoryKey),
  };
  const keyError = !mustRename
    ? null
    : trimmedKey.length === 0
    ? "This field is required"
    : takenKeys.has(trimmedKey)
    ? "This key is already in use"
    : null;

  const handleClose = () => {
    setSelectedKey(getExampleOfferingCategory("grade").key);
    setKeyDrafts({});
    onClose();
  };

  const handleCreate = async () => {
    if (keyError) return;

    const key = (mustRename ? trimmedKey : selected.key).trim();
    setCreatingKey(selected.key);
    try {
      await createOfferingCategories({
        path: { enrollmentPeriodId },
        body: [
          {
            label: selected.label,
            key,
            options: previewCategory.options.map((option) => ({
              label: option.label,
              key: option.key,
            })),
          },
        ],
      });
      toast({
        id: "quick-create-offering-category",
        title: "Offering category created",
      });
      await queryClient.invalidateQueries({
        queryKey: [FSQ.useGetOfferingCategoriesKey],
      });
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error({
        id: "quick-create-offering-category",
        title: "Failed to create offering category",
      });
    } finally {
      setCreatingKey(null);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        colorScheme="gray"
        leftIcon={<RiAddLine />}
        onClick={onOpen}
      >
        Quick create
      </Button>
      <ResizableDrawer
        isOpen={isOpen}
        onClose={handleClose}
        header="Quick create"
        contentProps={{ bg: "white", initialWidthPercent: 40 }}
        footer={
          <HStack spacing={2} w="full">
            <Button
              variant="outline"
              colorScheme="gray"
              flex={1}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              flex={1}
              isLoading={creatingKey === selected.key}
              isDisabled={keyError !== null}
              onClick={() => {
                void handleCreate();
              }}
            >
              Quick create
            </Button>
          </HStack>
        }
      >
        <VStack align="stretch" spacing={4} py={2}>
          <Text fontSize="sm" color="gray.600">
            Quickly create commonly used offering categories. Pick a template to
            create and then edit to fit your needs.
          </Text>
          <SegmentedControl
            options={EXAMPLE_OFFERING_CATEGORIES.map((category) => ({
              label: category.label,
              value: category.key,
              disabled: creatingKey !== null,
            }))}
            value={selected.key}
            onChange={setSelectedKey}
          />
          <ExampleOfferingCategoryCard
            category={previewCategory}
            keyContent={
              mustRename ? (
                <FormControl isInvalid={keyError !== null} mt={2}>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                    mb={1}
                  >
                    Offering category key
                  </FormLabel>
                  <Input
                    size="sm"
                    value={draftKey}
                    onChange={(event) => {
                      const nextKey = event.target.value;
                      setKeyDrafts((current) => ({
                        ...current,
                        [selected.key]: nextKey,
                      }));
                    }}
                  />
                  <FormErrorMessage>{keyError}</FormErrorMessage>
                </FormControl>
              ) : undefined
            }
          />
        </VStack>
      </ResizableDrawer>
    </>
  );
};
